"use client";

import type React from "react";
import { change, Field, reduxForm } from "redux-form";
import Button from "components/atoms/Button";
import "../PickUpForm/pickUp.scss";
import {
  Box,
  Grid,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  Popper,
} from "@mui/material";
import { date, required, requiredAutoComplete } from "utils/validate.util";
import AutocompleteRedux from "components/molecules/AutocompleteRedux";
import DateRangePickerReduxStatic from "components/molecules/DateRangePickerReduxStatic/DateRangePickerReduxStatic-2";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { mapAction } from "redux/slices/map/mapSlice";
import { useEffect, useRef, useState, useCallback } from "react";
import PersistReduxForm from "components/organisms/PersistReduxForm";
import { useTranslation } from "react-i18next";
import MapWithPin from "components/templates/MapWithPin";
import Pin from "assets/icons/Pin";
import TimePickerRedux30Min from "components/molecules/TimePickerRedux30Min/TimePickerRedux30Min";
import theme from "theme";
import { useNavigate } from "react-router-dom";
import CircleLoader from "components/atoms/CircleLoader";
import { formatDateForAPI } from "utils/calculatePrice.util";
import moment from "moment";
import { useScrollLock } from "hooks/useScrollLock";
import {
  getMobileFieldTriggerProps,
  MobileSearchDrawerField,
  SharedMobileFieldDrawer,
} from "../shared/mobileSearchDrawer";

interface OwnProps {
  differentReturnLocation: boolean;
  onDifferentReturnLocationChange: (checked: boolean) => void;
  onDrawerStateChange: (state: {
    pickup: boolean;
    destination: boolean;
  }) => void;
  forceModalBehavior?: boolean;
  disableDrawers?: boolean;
}
interface DeliveryFormProps extends OwnProps {
  handleSubmit: any;
}

function DeliveryForm({
  differentReturnLocation,
  onDifferentReturnLocationChange,
  onDrawerStateChange,
  disableDrawers = false,
  handleSubmit,
  forceModalBehavior = false,
}: DeliveryFormProps) {
  const form = "DeliveryForm";
  const navigate = useNavigate();
  const [formFocused, setFormFocused] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [pickupDateError, setPickupDateError] = useState<string>("");

  const open = useAppSelector((state) => state.map.open);
  const mapConfirmed = useAppSelector((state) => state.map.close);

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const today = new Date().toISOString().split("T")[0];

  const dispatch = useAppDispatch();
  const { i18n, t } = useTranslation();
  const show = useAppSelector((state) => state.map.show);
  const destination = useAppSelector(
    (state) => state.form?.[form]?.values?.destination,
  );
  const pickup = useAppSelector((state) => state.form?.[form]?.values?.pickup);
  const dateForm = useAppSelector((state) => state.form?.[form]?.values?.date);
  const pickupDate = useAppSelector(
    (state) => state.form?.[form]?.values?.pickupDate,
  );
  const endDateValue = useAppSelector(
    (state) => state.form?.[form]?.values?.endDate,
  );
  const pickupTime = useAppSelector(
    (state) => state.form?.[form]?.values?.pickupTime,
  );
  const endTime = useAppSelector(
    (state) => state.form?.[form]?.values?.endTime,
  );
  const timeValue = useAppSelector((state) => state.form?.[form]?.values?.time);
  const loading = useAppSelector((state) => state.delivery.loading);

  // Create refs
  const isAutoCorrectingPickupTime = useRef(false);
  const isAutoCorrectingEndTime = useRef(false);
  const prevPickup = useRef(pickup);
  const prevDestination = useRef(destination);
  const prevDateForm = useRef(dateForm);
  const prevTimeValue = useRef(timeValue);
  const prevEndDateValue = useRef(endDateValue);
  const prevPickupDate = useRef(pickupDate);
  const prevPickupTime = useRef(pickupTime);
  const prevEndDate = useRef(endDateValue);
  const prevEndTime = useRef(endTime);

  const startTimeInputRef = useRef<HTMLDivElement>(null);
  const endTimeInputRef = useRef<HTMLDivElement>(null);
  const dateInputRef = useRef<HTMLDivElement>(null);
  const destinationInputRef = useRef<HTMLDivElement>(null);
  const pickupInputRef = useRef<HTMLDivElement>(null);
  const endDateInputRef = useRef<HTMLDivElement>(null);

  // ADD THIS REF FOR THE FORM CONTAINER
  const outsideForm = useRef<HTMLDivElement>(null);
  const mapWrapperRef = useRef<HTMLDivElement>(null);

  const [activeMobileField, setActiveMobileField] =
    useState<MobileSearchDrawerField | null>(null);
  const drawerState = {
    pickup: activeMobileField === "pickup",
    destination: activeMobileField === "destination",
    pickupDate: activeMobileField === "pickupDate",
    pickupTime: activeMobileField === "pickupTime",
    endDate: activeMobileField === "endDate",
    endTime: activeMobileField === "endTime",
  };

  const shouldUseDrawers = isMobile && !disableDrawers;
  const useSingleFieldLayout = forceModalBehavior || isMobile;

  useScrollLock(
    !isMobile &&
      (Boolean(activeMobileField) || isDatePickerOpen || !!open),
  );

  const toggleDrawer = useCallback(
    (
      type:
        | "pickup"
        | "destination"
        | "pickupDate"
        | "pickupTime"
        | "endDate"
        | "endTime",
      state: boolean,
    ) => {
      setActiveMobileField(state ? type : null);
      onDrawerStateChange({
        pickup: state && type === "pickup",
        destination: state && type === "destination",
      });
    },
    [onDrawerStateChange],
  );
  const closeDrawer = useCallback(() => {
    const fieldIdMap: Partial<Record<MobileSearchDrawerField, string>> = {
      pickup: "pickup_delivery",
      destination: "destination_delivery",
      pickupDate: "date_delivery",
      pickupTime: "time_delivery",
      endDate: "endDate_delivery",
      endTime: "endTime_delivery",
    };
    const activeFieldId = activeMobileField ? fieldIdMap[activeMobileField] : null;
    if (activeFieldId) {
      const fieldRoot = document.getElementById(activeFieldId) as HTMLElement | null;
      fieldRoot?.blur?.();
      fieldRoot?.querySelector?.("input")?.blur?.();
    }
    (document.activeElement as HTMLElement | null)?.blur?.();
    setActiveMobileField(null);
    onDrawerStateChange({
      pickup: false,
      destination: false,
    });
    window.setTimeout(() => {
      (document.activeElement as HTMLElement | null)?.blur?.();
    }, 60);
  }, [activeMobileField, onDrawerStateChange]);
  const prevMapOpenRef = useRef(open);
  useEffect(() => {
    if (isMobile && prevMapOpenRef.current === true && open === false && mapConfirmed) {
      if (activeMobileField === "pickup" || activeMobileField === "destination") {
        closeDrawer();
      }
    }
    prevMapOpenRef.current = open;
  }, [open, isMobile, activeMobileField, closeDrawer, mapConfirmed]);

  const handleFieldClick = (
    type:
      | "pickup"
      | "destination"
      | "pickupDate"
      | "pickupTime"
      | "endDate"
      | "endTime",
  ) => {
    if (shouldUseDrawers) {
      toggleDrawer(type, true);
      if (type === "pickup") {
        dispatch(mapAction.setOpen(false));
        dispatch(mapAction.setShow(0));
        dispatch(mapAction.setdestinationMap(false));
      }
      if (type === "destination") {
        dispatch(mapAction.setOpen(false));
        dispatch(mapAction.setShow(1));
        dispatch(mapAction.setdestinationMap(false));
      }
    } else {
      // DESKTOP: Just toggle the map and set the show state
      if (type === "pickup") {
        dispatch(mapAction.setShow(0));
        dispatch(mapAction.setdestinationMap(false));
      } else if (type === "destination") {
        dispatch(mapAction.setShow(1));
        dispatch(mapAction.setdestinationMap(false));
      }
    }
  };
  const triggerMobileDrawer = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
      field:
        | "pickup"
        | "destination"
        | "pickupDate"
        | "pickupTime"
        | "endDate"
        | "endTime",
    ) => {
      if (!shouldUseDrawers) return;
      event.preventDefault();
      event.stopPropagation();
      handleFieldClick(field);
    },
    [handleFieldClick, shouldUseDrawers],
  );

  // ADD OUTSIDE CLICK HANDLER
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;

      const anyDrawerOpen = Boolean(activeMobileField);
      if (anyDrawerOpen) return;

      const isInsideMap = mapWrapperRef.current?.contains(target);
      const isInsideForm = outsideForm.current?.contains(target);

      if (!isInsideForm && !isInsideMap) {
        dispatch(mapAction.setOpen(false));
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [activeMobileField, dispatch]);

  // Auto-focus after pickup address is selected
  useEffect(() => {
    if (isMobile) {
      prevPickup.current = pickup;
      return;
    }

    if (formFocused && pickup?.details && pickup !== prevPickup.current) {
      if (!differentReturnLocation) {
        dispatch(change(form, "destination", pickup));
        closeDrawer();
        dispatch(mapAction.setOpen(false));

        setTimeout(() => {
          if (dateInputRef.current) {
            const input = dateInputRef.current.querySelector("input");
            if (input) {
              input.focus();
              input.click();
            }
          }
        }, 200);
      } else {
        closeDrawer();

        setTimeout(() => {
          if (destinationInputRef.current) {
            const input = destinationInputRef.current.querySelector("input");
            if (input) {
              input.focus();
              input.click();
            }
          }
        }, 200);
      }
    }
    prevPickup.current = pickup;
  }, [
    pickup,
    differentReturnLocation,
    formFocused,
    dispatch,
    form,
    toggleDrawer,
    closeDrawer,
    isMobile,
  ]);

  useEffect(() => {
    if (differentReturnLocation) return;

    const pickupPlaceId = pickup?.details?.place_id;
    const destinationPlaceId = destination?.details?.place_id;

    if (!pickup?.details) {
      if (destination) {
        dispatch(change(form, "destination", null));
      }
      return;
    }

    if (pickupPlaceId && pickupPlaceId === destinationPlaceId) {
      return;
    }

    if (pickup !== destination) {
      dispatch(change(form, "destination", pickup));
    }
  }, [destination, differentReturnLocation, dispatch, form, pickup]);

  useEffect(() => {
    if (isMobile) {
      prevDestination.current = destination;
      return;
    }

    if (formFocused) {
      if (destination?.details && destination !== prevDestination.current) {
        closeDrawer();
        dispatch(mapAction.setOpen(false));

        if (differentReturnLocation) {
          setTimeout(() => {
            if (dateInputRef.current) {
              const input = dateInputRef.current.querySelector("input");
              if (input) {
                input.focus();
                input.click();
              }
            }
          }, 200);
        }
      }
    }
    prevDestination.current = destination;
  }, [
    destination,
    formFocused,
    dispatch,
    differentReturnLocation,
    closeDrawer,
    isMobile,
  ]);

  useEffect(() => {
    if (isMobile) {
      prevPickupDate.current = pickupDate;
      prevEndDate.current = endDateValue;
      return;
    }

    if (
      formFocused &&
      !isDatePickerOpen &&
      pickupDate?.date &&
      endDateValue?.date &&
      (pickupDate !== prevPickupDate.current ||
        endDateValue !== prevEndDate.current)
    ) {
      setTimeout(() => {
        const startTimeInput = document.getElementById("time_delivery");
        if (startTimeInput) {
          startTimeInput.focus();
          startTimeInput.click();
        }
      }, 200);
    }
    prevPickupDate.current = pickupDate;
    prevEndDate.current = endDateValue;
  }, [pickupDate, endDateValue, formFocused, isDatePickerOpen, isMobile]);

  useEffect(() => {
    if (isMobile) {
      prevPickupTime.current = pickupTime;
      return;
    }

    if (
      formFocused &&
      pickupTime &&
      pickupTime !== prevPickupTime.current &&
      !isAutoCorrectingPickupTime.current // ADD THIS CHECK
    ) {
      setTimeout(() => {
        const endTimeInput = document.getElementById("endTime_delivery");
        if (endTimeInput) {
          endTimeInput.focus();
          endTimeInput.click();
        }
      }, 150);
    }
    prevPickupTime.current = pickupTime;
  }, [pickupTime, formFocused, isMobile]);

  useEffect(() => {
    prevDateForm.current = dateForm;
  }, [dateForm]);

  useEffect(() => {
    prevTimeValue.current = timeValue;
  }, [timeValue]);

  useEffect(() => {
    prevEndDateValue.current = endDateValue;
  }, [endDateValue]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const dateInputs = [
          document.getElementById("date_delivery"),
          document.getElementById("endDate_delivery"),
        ];

        dateInputs.forEach((input) => {
          if (input) {
            input.blur();
          }
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Sync form values to localStorage
  useEffect(() => {
    const formValues = {
      pickup: pickup,
      destination: destination,
      pickupDate: pickupDate,
      endDate: endDateValue,
      pickupTime: pickupTime,
      endTime: endTime,
    };

    let pickupDateFormatted = "";
    let dropOffDateFormatted = "";

    if (pickupDate?.date && pickupTime) {
      try {
        const pickupDateStr = formatDateForAPI(pickupDate);
        const dateTimeStr = `${pickupDateStr}T${pickupTime}:00.000Z`;
        pickupDateFormatted = dateTimeStr;
      } catch (e) {
        console.error("Error formatting pickup date:", e);
      }
    }

    if (endDateValue?.date && endTime) {
      try {
        const dropOffDateStr = formatDateForAPI(endDateValue);
        const dateTimeStr = `${dropOffDateStr}T${endTime}:00.000Z`;
        dropOffDateFormatted = dateTimeStr;
      } catch (e) {
        console.error("Error formatting end date:", e);
      }
    }

    const formattedValues = {
      ...formValues,
      pickupDateFormatted,
      dropOffDateFormatted,
    };

    if (pickup) {
      localStorage.setItem("DeliveryForm", JSON.stringify(formattedValues));
      window.dispatchEvent(new Event("deliveryFormUpdated"));
    }
  }, [pickup, destination, pickupDate, endDateValue, pickupTime, endTime]);
  const handleDeliverySubmit = (values: any) => {
    const pickupDateStr = formatDateForAPI(values.pickupDate);
    const dropOffDateStr = formatDateForAPI(values.endDate);
    if (!pickupDateStr || !dropOffDateStr) {
      console.error("Pickup or End date is missing");
      return;
    }

    if (!pickupDateStr || pickupDateStr < today) {
      setPickupDateError(
        t("selectFutureDate") || "Please select a future date",
      );
      return;
    }

    setPickupDateError("");
    window.dispatchEvent(new Event("deliveryFormUpdated"));
    navigate("/search-delivery-form");
  };

  useEffect(() => {
    if (pickupDate?.date) setPickupDateError("");
  }, [pickupDate]);

  useEffect(() => {
    if (!isMobile) {
      prevPickup.current = pickup;
      prevDestination.current = destination;
      prevPickupDate.current = pickupDate;
      prevPickupTime.current = pickupTime;
      prevEndDate.current = endDateValue;
      prevEndTime.current = endTime;
      return;
    }

    if (
      activeMobileField === "pickup" &&
      pickup?.details &&
      pickup !== prevPickup.current
    ) {
      closeDrawer();
    }

    if (
      activeMobileField === "destination" &&
      destination?.details &&
      destination !== prevDestination.current
    ) {
      closeDrawer();
    }

    if (
      activeMobileField === "pickupDate" &&
      pickupDate?.date &&
      endDateValue?.date &&
      endDateValue !== prevEndDate.current
    ) {
      closeDrawer();
    }

    if (
      activeMobileField === "pickupTime" &&
      pickupTime &&
      pickupTime !== prevPickupTime.current
    ) {
      closeDrawer();
    }

    if (
      activeMobileField === "endDate" &&
      endDateValue?.date &&
      endDateValue !== prevEndDate.current
    ) {
      closeDrawer();
    }

    if (
      activeMobileField === "endTime" &&
      endTime &&
      endTime !== prevEndTime.current
    ) {
      closeDrawer();
    }

    prevPickup.current = pickup;
    prevDestination.current = destination;
    prevPickupDate.current = pickupDate;
    prevPickupTime.current = pickupTime;
    prevEndDate.current = endDateValue;
    prevEndTime.current = endTime;
  }, [
    activeMobileField,
    closeDrawer,
    destination,
    endDateValue,
    endTime,
    isMobile,
    pickup,
    pickupDate,
    pickupTime,
  ]);

  const pickupTriggerProps = getMobileFieldTriggerProps(
    shouldUseDrawers,
    "pickup",
    handleFieldClick,
  );
  const destinationTriggerProps = getMobileFieldTriggerProps(
    shouldUseDrawers,
    "destination",
    handleFieldClick,
  );
  const pickupDateTriggerProps = getMobileFieldTriggerProps(
    shouldUseDrawers,
    "pickupDate",
    handleFieldClick,
  );
  const pickupTimeTriggerProps = getMobileFieldTriggerProps(
    shouldUseDrawers,
    "pickupTime",
    handleFieldClick,
  );
  const endDateTriggerProps = getMobileFieldTriggerProps(
    shouldUseDrawers,
    "endDate",
    handleFieldClick,
  );
  const endTimeTriggerProps = getMobileFieldTriggerProps(
    shouldUseDrawers,
    "endTime",
    handleFieldClick,
  );

  const mobileDrawerTitle = (() => {
    switch (activeMobileField) {
      case "pickup":
        return !differentReturnLocation
          ? t("delivery_and_return_address")
          : t("delivery");
      case "destination":
        return t("return");
      case "pickupDate":
        return t("deliveryDate");
      case "pickupTime":
        return t("deliveryTime");
      case "endDate":
        return t("returnDate");
      case "endTime":
        return t("returnTime");
      default:
        return "";
    }
  })();

  const renderMobileDrawerContent = () => {
    switch (activeMobileField) {
      case "pickup":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <Box sx={{ flex: open ? "0 0 auto" : 1, maxHeight: open ? "60px" : undefined, minHeight: 0, display: "flex", overflow: "hidden" }}>
              <Field
                id="pickup_mobile_delivery_editor"
                name="pickup"
                label={t("pickUp")}
                validate={[requiredAutoComplete]}
                component={AutocompleteRedux}
                StartIcon={Pin}
                searchForm={true}
                renderDrawerContentOnly
                drawerInputAutoFocus
                AutocompleteProps={{
                  onSelectionComplete: closeDrawer,
                }}
              />
            </Box>
            {open && (
              <Box sx={{ flex: 1, minHeight: 0, borderTop: "1px solid #ECEBF2" }}>
                <MapWithPin />
              </Box>
            )}
          </Box>
        );
      case "destination":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <Box sx={{ flex: open ? "0 0 auto" : 1, maxHeight: open ? "60px" : undefined, minHeight: 0, display: "flex", overflow: "hidden" }}>
              <Field
                id="destination_mobile_delivery_editor"
                name="destination"
                label={t("destination")}
                validate={[requiredAutoComplete]}
                component={AutocompleteRedux}
                StartIcon={Pin}
                searchForm={true}
                renderDrawerContentOnly
                drawerInputAutoFocus
                AutocompleteProps={{
                  onSelectionComplete: closeDrawer,
                }}
              />
            </Box>
            {open && (
              <Box sx={{ flex: 1, minHeight: 0, borderTop: "1px solid #ECEBF2" }}>
                <MapWithPin />
              </Box>
            )}
          </Box>
        );
      case "pickupDate":
        return (
          <Box sx={{ p: 2, flex: 1, minHeight: 0, overflowY: "auto" }}>
            <Field
              id="pickupDate_mobile_editor"
              name="pickupDate"
              label={t("deliveryDate")}
              validate={[date]}
              component={DateRangePickerReduxStatic}
              renderPickerOnly
              DateRangePickerProps={{
                disablePast: true,
              }}
              formName={form}
              onSelectionComplete={closeDrawer}
            />
          </Box>
        );
      case "pickupTime":
        return (
          <Box
            sx={{
              p: 2,
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Field
              id="pickupTime_mobile_editor"
              name="pickupTime"
              label={t("deliveryTime")}
              validate={[required]}
              component={TimePickerRedux30Min}
              selectedDate={moment(pickupDate?.date).local()?.format("YYYY-MM-DD")}
              TimePickerProps={{
                renderPickerOnly: true,
                isDropoff: false,
                pickupTime: pickupTime,
                pickupDate: moment(pickupDate?.date).local()?.format("YYYY-MM-DD"),
                onSelectionComplete: closeDrawer,
                onClose: closeDrawer,
              }}
            />
          </Box>
        );
      case "endDate":
        return (
          <Box sx={{ p: 2, flex: 1, minHeight: 0, overflowY: "auto" }}>
            <Field
              id="endDate_mobile_editor"
              name="endDate"
              label={t("returnDate")}
              validate={[date]}
              component={DateRangePickerReduxStatic}
              renderPickerOnly
              DateRangePickerProps={{
                disablePast: true,
              }}
              formName={form}
              onSelectionComplete={closeDrawer}
            />
          </Box>
        );
      case "endTime":
        return (
          <Box
            sx={{
              p: 2,
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Field
              id="endTime_mobile_editor"
              name="endTime"
              label={t("returnTime")}
              validate={[required]}
              component={TimePickerRedux30Min}
              selectedDate={moment(endDateValue?.date).local()?.format("YYYY-MM-DD")}
              TimePickerProps={{
                renderPickerOnly: true,
                isDropoff: true,
                pickupTime: pickupTime,
                pickupDate: moment(pickupDate?.date).local()?.format("YYYY-MM-DD"),
                onSelectionComplete: closeDrawer,
                onClose: closeDrawer,
              }}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {loading && <CircleLoader />}
      <div style={{ position: "relative", padding: "16px" }} ref={outsideForm}>
        {!forceModalBehavior && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={differentReturnLocation}
                onChange={(e) => {
                  onDifferentReturnLocationChange(e.target.checked);
                  if (!e.target.checked) {
                    if (pickup?.details) {
                      dispatch(change(form, "destination", pickup));
                    } else {
                      dispatch(change(form, "destination", null));
                    }
                  }
                }}
                sx={{
                  color: "#000000",
                  "&.Mui-checked": {
                    color: "#520A76",
                    fontSize: "14px",
                  },
                  position: "relative",
                  bottom: "2px",
                }}
              />
            }
            label={
              <span
                style={{
                  color: differentReturnLocation ? "#520A76" : " #000000 ",
                }}
              >
                {t("returnFromDifferentLocation") ||
                  "Return to a different location"}
              </span>
            }
          />
        )}

        <Grid
          container={!useSingleFieldLayout}
          item={useSingleFieldLayout}
          spacing={useSingleFieldLayout ? 0 : 3}
          alignItems="center"
          justifyContent="center"
          xs={useSingleFieldLayout ? 12 : undefined}
          sm={useSingleFieldLayout ? 12 : undefined}
          sx={{ py: 1 }}
          onFocus={() => setFormFocused(true)}
        >
          <Grid
            item
            xs={12}
            sm={useSingleFieldLayout ? 12 : 6}
            md={differentReturnLocation ? 2.5 : 5}
            sx={{ mb: useSingleFieldLayout ? 2 : 0 }}
            ref={pickupInputRef}
          >
            <div
              className={
                i18n.language === "en"
                  ? "pickUp__field"
                  : "pickUp__field arPickUp__field"
              }
              onClickCapture={(event) => triggerMobileDrawer(event, "pickup")}
              style={
                open && show === 0
                  ? {
                      borderColor: "#520A76",
                      ...(i18n.language === "en"
                        ? {
                            borderRight: !differentReturnLocation
                              ? "1px solid #520A76"
                              : "1px solid #520A76",
                          }
                        : {
                            borderLeft: !differentReturnLocation
                              ? "none"
                              : "1px solid #520A76",
                          }),
                    }
                  : {}
              }
            >
              <Field
                onClick={() => handleFieldClick("pickup")}
                id="pickup_delivery"
                name="pickup"
                label={
                  !differentReturnLocation
                    ? t("delivery_and_return_address")
                    : t("delivery")
                }
                placeholder={t("enterLocation")}
                validate={[requiredAutoComplete]}
                component={AutocompleteRedux}
                disableInitialDropdown={forceModalBehavior}
                mobileTriggerProps={pickupTriggerProps}
              />
            </div>
          </Grid>

          {differentReturnLocation && (
            <Grid
              item
              xs={12}
              sm={useSingleFieldLayout ? 12 : 6}
              md={2.5}
              sx={{ mb: useSingleFieldLayout ? 2 : 0 }}
              ref={destinationInputRef}
              style={
                !useSingleFieldLayout && i18n.language === "en"
                  ? { paddingLeft: 0 }
                  : !useSingleFieldLayout && i18n.language === "ar"
                    ? { paddingRight: 0 }
                    : {}
              }
            >
              <div
                className={
                  i18n.language === "en"
                    ? "destination__field"
                    : "destination__field arDestination__field"
                }
                onClickCapture={(event) =>
                  triggerMobileDrawer(event, "destination")
                }
                style={
                  open && show === 1
                    ? {
                        borderColor: "#520A76",
                        ...(i18n.language === "en"
                          ? { borderLeft: "1px solid #520A76" }
                          : { borderRight: "1px solid #520A76" }),
                      }
                    : {}
                }
              >
                <Field
                  onClick={() => handleFieldClick("destination")}
                  id="destination_delivery"
                  name="destination"
                  label={t("return")}
                  placeholder={t("enterLocation")}
                  validate={[requiredAutoComplete]}
                  component={AutocompleteRedux}
                  disableInitialDropdown={forceModalBehavior}
                  mobileTriggerProps={destinationTriggerProps}
                />
              </div>
            </Grid>
          )}

          {/* Date and Time fields */}
          <Grid
            item
            xs={12}
            sm={useSingleFieldLayout ? 12 : 6}
            md={useSingleFieldLayout ? 12 : 1.5}
            sx={{ mb: useSingleFieldLayout ? 2 : 0 }}
          >
            {useSingleFieldLayout ? (
              <Grid container spacing={1}>
                <Grid item xs={6} ref={dateInputRef}>
                  <div>
                    <div onClickCapture={(event) => triggerMobileDrawer(event, "pickupDate")}>
                    <Field
                      onClick={() => dispatch(mapAction.setOpen(false))}
                      id="date_delivery"
                      name="pickupDate"
                      label={t("deliveryDate")}
                      validate={[date]}
                      component={DateRangePickerReduxStatic}
                      onOpenChange={setIsDatePickerOpen}
                      disableInternalOpen={shouldUseDrawers}
                      triggerOnlyProps={pickupDateTriggerProps}
                      DateRangePickerProps={{
                        disablePast: true,
                      }}
                      formName={form}
                    />
                    </div>
                    {pickupDateError && (
                      <span
                        style={{
                          color: "#d32f2f",
                          fontSize: "10px",
                          marginLeft: "4px",
                          position: "absolute",
                        }}
                      >
                        {pickupDateError}
                      </span>
                    )}
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div
                    className={
                      i18n.language === "en"
                        ? "time__field modal-single-field"
                        : "time__field arTime__field modal-single-field"
                    }
                    onClickCapture={(event) =>
                      triggerMobileDrawer(event, "pickupTime")
                    }
                  >
                    <Field
                      onClick={() => dispatch(mapAction.setOpen(false))}
                      id="time_delivery"
                      name="pickupTime"
                      label={t("deliveryTime")}
                      validate={[required]}
                      component={TimePickerRedux30Min}
                      selectedDate={moment(pickupDate?.date)
                        .local()
                        ?.format("YYYY-MM-DD")}
                      TimePickerProps={{
                        disableInternalOpen: shouldUseDrawers,
                        triggerOnlyProps: pickupTimeTriggerProps,
                        customLabelStyles: true,
                        labelShrink: true,
                        InputFieldProps: {
                          id: "time_delivery",
                        },
                        isDropoff: false,
                      }}
                    />
                  </div>
                </Grid>
              </Grid>
            ) : (
              <>
                <div ref={dateInputRef}>
                  <div onClickCapture={(event) => triggerMobileDrawer(event, "pickupDate")}>
                  <Field
                    onClick={() => dispatch(mapAction.setOpen(false))}
                    id="date_delivery"
                    name="pickupDate"
                    label={t("deliveryDate")}
                    validate={[date]}
                    component={DateRangePickerReduxStatic}
                    onOpenChange={setIsDatePickerOpen}
                    disableInternalOpen={shouldUseDrawers}
                    triggerOnlyProps={pickupDateTriggerProps}
                    DateRangePickerProps={{
                      disablePast: true,
                    }}
                    formName={form}
                  />
                  </div>
                  {pickupDateError && (
                    <span
                      style={{
                        color: "#d32f2f",
                        fontSize: "10px",
                        position: "absolute",
                      }}
                    >
                      {pickupDateError}
                    </span>
                  )}
                </div>
              </>
            )}
          </Grid>

          {!useSingleFieldLayout && (
            <Grid
              item
              xs={12}
              sm={6}
              md={1.5}
              sx={{ mb: 0 }}
              style={
                i18n.language === "en"
                  ? { paddingLeft: 0 }
                  : { paddingRight: 0 }
              }
            >
              <div
                ref={startTimeInputRef}
                className={
                  i18n.language === "en"
                    ? "time__field"
                    : "time__field arTime__field"
                }
                onClickCapture={(event) => triggerMobileDrawer(event, "pickupTime")}
              >
                <Field
                  onClick={() => dispatch(mapAction.setOpen(false))}
                  id="time_delivery"
                  name="pickupTime"
                  label={t("deliveryTime")}
                  validate={[required]}
                  component={TimePickerRedux30Min}
                  selectedDate={moment(pickupDate?.date)
                    .local()
                    ?.format("YYYY-MM-DD")}
                  TimePickerProps={{
                    disableInternalOpen: shouldUseDrawers,
                    triggerOnlyProps: pickupTimeTriggerProps,
                    customLabelStyles: true,
                    labelShrink: true,
                    InputFieldProps: {
                      id: "time_delivery",
                    },
                    isDropoff: false,
                    pickupTime: pickupTime,
                    pickupDate: moment(pickupDate?.date)
                      .local()
                      ?.format("YYYY-MM-DD"),
                    onAutoCorrect: (isAutoCorrect: boolean) => {
                      isAutoCorrectingPickupTime.current = isAutoCorrect;
                    },
                  }}
                />
              </div>
            </Grid>
          )}

          <Grid
            item
            xs={12}
            sm={useSingleFieldLayout ? 12 : 6}
            md={useSingleFieldLayout ? 12 : 1.5}
            sx={{ mb: useSingleFieldLayout ? 2 : 0 }}
          >
            {useSingleFieldLayout ? (
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <div onClickCapture={(event) => triggerMobileDrawer(event, "endDate")}>
                  <Field
                    onClick={() => dispatch(mapAction.setOpen(false))}
                    id="endDate_delivery"
                    name="endDate"
                    label={t("returnDate")}
                    validate={[date]}
                    component={DateRangePickerReduxStatic}
                    onOpenChange={setIsDatePickerOpen}
                    disableInternalOpen={shouldUseDrawers}
                    triggerOnlyProps={endDateTriggerProps}
                    DateRangePickerProps={{
                      disablePast: true,
                    }}
                    formName={form}
                  />
                  </div>
                </Grid>
                <Grid item xs={6} ref={endTimeInputRef}>
                  <div
                    className={
                      i18n.language === "en"
                        ? "time__field modal-single-field"
                        : "time__field arTime__field modal-single-field"
                    }
                    onClickCapture={(event) =>
                      triggerMobileDrawer(event, "endTime")
                    }
                  >
                    <Field
                      onClick={() => dispatch(mapAction.setOpen(false))}
                      id="endTime_delivery"
                      name="endTime"
                      label={t("returnTime")}
                      validate={[required]}
                      component={TimePickerRedux30Min}
                      selectedDate={moment(endDateValue?.date)
                        .local()
                        ?.format("YYYY-MM-DD")}
                      TimePickerProps={{
                        disableInternalOpen: shouldUseDrawers,
                        triggerOnlyProps: endTimeTriggerProps,
                        customLabelStyles: true,
                        labelShrink: true,
                        InputFieldProps: {
                          id: "endTime_delivery",
                        },
                        isDropoff: true,
                        pickupTime: pickupTime,
                        pickupDate: moment(pickupDate?.date)
                          .local()
                          ?.format("YYYY-MM-DD"),
                        onAutoCorrect: (isAutoCorrect: boolean) => {
                          isAutoCorrectingEndTime.current = isAutoCorrect;
                        },
                      }}
                    />
                  </div>
                </Grid>
              </Grid>
            ) : (
              <div onClickCapture={(event) => triggerMobileDrawer(event, "endDate")}>
                <Field
                  onClick={() => dispatch(mapAction.setOpen(false))}
                  id="endDate_delivery"
                  name="endDate"
                  label={t("returnDate")}
                  validate={[date]}
                  component={DateRangePickerReduxStatic}
                  onOpenChange={setIsDatePickerOpen}
                  disableInternalOpen={shouldUseDrawers}
                  triggerOnlyProps={endDateTriggerProps}
                  DateRangePickerProps={{
                    disablePast: true,
                  }}
                  formName={form}
                />
              </div>
            )}
          </Grid>

          {!useSingleFieldLayout && (
            <Grid
              item
              xs={12}
              sm={6}
              md={1.5}
              sx={{ mb: 0 }}
              ref={endTimeInputRef}
              style={
                i18n.language === "en"
                  ? { paddingLeft: 0 }
                  : { paddingRight: 0 }
              }
            >
              <div
                className={
                  i18n.language === "en"
                    ? "time__field"
                    : "time__field arTime__field"
                }
                onClickCapture={(event) => triggerMobileDrawer(event, "endTime")}
              >
                <Field
                  onClick={() => dispatch(mapAction.setOpen(false))}
                  id="endTime_delivery"
                  name="endTime"
                  label={t("returnTime")}
                  validate={[required]}
                  component={TimePickerRedux30Min}
                  selectedDate={moment(endDateValue?.date)
                    .local()
                    ?.format("YYYY-MM-DD")}
                  TimePickerProps={{
                    disableInternalOpen: shouldUseDrawers,
                    triggerOnlyProps: endTimeTriggerProps,
                    customLabelStyles: true,
                    labelShrink: true,
                    InputFieldProps: {
                      id: "endTime_delivery",
                    },
                    isDropoff: true,
                    pickupTime: pickupTime,
                    pickupDate: moment(pickupDate?.date)
                      .local()
                      ?.format("YYYY-MM-DD"),
                  }}
                />
              </div>
            </Grid>
          )}

          {!forceModalBehavior && (
            <Grid
              item
              xs={12}
              sm={6}
              md={1}
              display="flex"
              justifyContent="center"
              style={{ paddingLeft: 8 }}
            >
              <Button
                id="search-delivery-button"
                sx={{
                  width: "100%",
                  maxWidth: "152px",
                  height: "65px",
                  borderRadius: "10px",
                  backgroundColor: "#520A76",
                  color: "white",
                  fontWeight: 500,
                  fontSize: "16px",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#6A1B9A" },
                }}
                variant="contained"
                onClick={handleSubmit(handleDeliverySubmit)}
              >
                {t("search")}
              </Button>
            </Grid>
          )}
        </Grid>

        {shouldUseDrawers && activeMobileField && (
          <SharedMobileFieldDrawer
            open={Boolean(activeMobileField)}
            onClose={closeDrawer}
            title={mobileDrawerTitle}
          >
            {renderMobileDrawerContent()}
          </SharedMobileFieldDrawer>
        )}

        {/* ADD POPPER MAP FOR DESKTOP */}
        <Popper
          open={!!open && !isMobile}
          anchorEl={outsideForm.current}
          placement={i18n.language === "en" ? "bottom-start" : "bottom-end"}
          style={{ zIndex: 1600 }}
          modifiers={[
            { name: "offset", options: { offset: [15, -10] } },
            { name: "preventOverflow", options: { padding: 16 } },
          ]}
        >
          <Box
            ref={mapWrapperRef}
            sx={{
              bgcolor: "#fff",
              borderRadius: "16px",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.25)",
              width: isMobile ? "95vw" : 850,
              height: isMobile ? "70vh" : 368,
              overflow: "hidden",
            }}
          >
            <MapWithPin />
          </Box>
        </Popper>

        <PersistReduxForm form={form} />
      </div>
    </>
  );
}

export default reduxForm<{}, OwnProps>({
  form: "DeliveryForm",
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(DeliveryForm);
